// People grouping and filtering helpers.
// All are el.call()-compatible (regular functions, `this` binds to element).

import { groupByRole } from './roleHelpers.js'
import { LEGACY_ORG_IDS, effectiveCategory } from './permissions.js'

const GROUP_LABEL = {}

export function groupLabel (raw) {
  return GROUP_LABEL[raw] || (raw || 'Other').replace(/^./, c => c.toUpperCase())
}

// Resolve a person's primary team name via the SDK-sourced
// `teamsByEmail` map. Returns 'Other' for people with no team
// membership — the legacy Supabase `user_profiles.department` /
// `group_name` columns were dropped in migration 0055.
export function groupKeyOf (person, teamsByEmail) {
  const email = String(person?.email || '').toLowerCase()
  const teams = teamsByEmail?.[email] || []
  return teams[0]?.name || 'Other'
}

// Clip `public.people` (cross-tenant) down to the active org's roster.
// NEVER return unfiltered people for a known active org — that leaks
// cross-tenant users. Legacy orgs bypass via LEGACY_ORG_IDS.
const _activeOrgPeople = (root) => {
  const people = root?.people || []
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return people
  if (LEGACY_ORG_IDS.has(orgId)) return people
  const set = root?.orgMembersByOrgId?.[orgId]
  if (!set) {
    const myEmail = String(root?.currentUserEmail || '').toLowerCase()
    if (!myEmail) return []
    return people.filter((p) => String(p?.email || '').toLowerCase() === myEmail)
  }
  return people.filter((p) => !!(set[String(p?.email || '').toLowerCase()]))
}

// Returns all team categories with user counts.
// el.call('peopleGroupCategories')
export function peopleGroupCategories () {
  const root = this && this.state && this.state.root
  const people = _activeOrgPeople(root)
  const counts = new Map()
  for (const p of people) {
    const k = groupKeyOf(p, root?.teamsByEmail)
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ key, label: groupLabel(key), count, name: key, kind: 'group' }))
}

// Returns all roles with user counts derived from root.roles.byUser.
// CU-10 §90: roles no longer on person object; source is root.roles.byUser.
// el.call('peopleRoleCategories')
// CU-10 §90: public.roles view dropped (migration 0087). Role order/labels
// fall back to the canonical hardcoded list; counts derived from byUser.
const ROLE_FALLBACK_ORDER = ['superadmin', 'admin', 'manager', 'member', 'advisor', 'investor']
const ROLE_FALLBACK_LABELS = { superadmin: 'Superadmin', admin: 'Admin', manager: 'Manager', member: 'Member', advisor: 'Advisor', investor: 'Investor' }

export function peopleRoleCategories () {
  const root = this && this.state && this.state.root
  const people = _activeOrgPeople(root)
  const byUser = root?.roles?.byUser || {}
  const counts = new Map()
  for (const p of people) {
    // Legacy fallback for pre-§90 data still in p.roles
    const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
    if (legacyRoles) {
      for (const r of legacyRoles) counts.set(r, (counts.get(r) || 0) + 1)
    } else {
      const role = byUser[p.userId] || 'member'
      counts.set(role, (counts.get(role) || 0) + 1)
    }
  }
  return ROLE_FALLBACK_ORDER
    .filter(r => counts.has(r))
    .map(r => ({
      key: r,
      label: ROLE_FALLBACK_LABELS[r] || r.replace(/^./, c => c.toUpperCase()),
      count: counts.get(r) || 0,
      name: r,
      kind: 'role'
    }))
}

// Team-category filter options with counts. el.call('peopleTeamCategories')
export function peopleTeamCategories () {
  const root = this && this.state && this.state.root
  const teamsByEmail = root?.teamsByEmail || {}
  const counts = { active: 0, external: 0, none: 0 }
  for (const p of _activeOrgPeople(root)) {
    const email = String(p?.email || '').toLowerCase()
    counts[effectiveCategory(teamsByEmail[email])] += 1
  }
  const out = [
    { key: 'active', label: 'Active', count: counts.active, name: 'active', kind: 'category' },
    { key: 'external', label: 'External', count: counts.external, name: 'external', kind: 'category' }
  ]
  if (counts.none > 0) {
    out.push({ key: 'none', label: 'Unassigned', count: counts.none, name: 'none', kind: 'category' })
  }
  return out
}

// Filter people by active-org membership + selected group and/or role.
// `public.people` is a cross-tenant view (user_profiles has no
// workspace_id); scoping down to the active org's member set is how
// tenancy gets enforced for identity surfaces. `orgMembersByOrgId` is
// populated by `loadAllTeams` on sign-in. Legacy orgs bypass the
// OrgMember intersection (see LEGACY_ORG_IDS).
// el.call('filterPeople')
export function filterPeople () {
  const root = this && this.state && this.state.root
  const people = root?.people || []
  const g = root?.selectedGroup
  const r = root?.selectedRole
  const c = root?.selectedTeamCategory  // 'active' | 'external' | 'none' | null
  const activeOrgId = root?.jwtClaims?.workspace_org_id
  const isLegacy = activeOrgId && LEGACY_ORG_IDS.has(activeOrgId)
  const orgMembers = activeOrgId && !isLegacy ? root?.orgMembersByOrgId?.[activeOrgId] : null
  const teamsByEmail = root?.teamsByEmail || {}
  // CU-10 §90: role per person from root.roles.byUser (not p.roles)
  const byUser = root?.roles?.byUser || {}
  return people.filter(p => {
    if (orgMembers && !!!(orgMembers[String(p?.email || '').toLowerCase()])) return false
    if (g && groupKeyOf(p, teamsByEmail) !== g) return false
    if (r) {
      // Legacy fallback: person still has .roles array
      const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
      if (legacyRoles) {
        if (legacyRoles.length === 0 && r !== 'member') return false
        if (legacyRoles.length > 0 && !legacyRoles.includes(r)) return false
      } else {
        const assignedRole = byUser[p.userId] || 'member'
        if (assignedRole !== r) return false
      }
    }
    if (c) {
      const email = String(p?.email || '').toLowerCase()
      const cat = effectiveCategory(teamsByEmail[email])
      if (cat !== c) return false
    }
    return true
  })
}

// Group filtered people by team (default) or by active filter. When a
// role or category filter is active, shows a single group. Team-first
// model replaces the legacy role-first grouping (I11).
// el.call('groupFilteredPeople')
export function groupFilteredPeople () {
  const root = this && this.state && this.state.root
  const filtered = filterPeople.call(this)

  // Role filter active — single group labelled with the role
  if (root?.selectedRole) {
    const available = root?.roles?.available || []
    const meta = available.find(r => r.name === root.selectedRole)
    return [{ name: meta?.label || root.selectedRole, people: filtered }]
  }

  // Category filter active — single group labelled with the category
  if (root?.selectedTeamCategory) {
    const label = root.selectedTeamCategory.charAt(0).toUpperCase() + root.selectedTeamCategory.slice(1)
    return [{ name: label, people: filtered }]
  }

  // Default (including explicit team filter) — group by team name
  const buckets = new Map()
  for (const p of filtered) {
    const k = groupKeyOf(p, root?.teamsByEmail)
    if (!buckets.has(k)) buckets.set(k, [])
    buckets.get(k).push(p)
  }
  // Sort: named teams alphabetically first, then "Other" last
  return [...buckets.entries()]
    .sort((a, b) => {
      if (a[0] === 'Other') return 1
      if (b[0] === 'Other') return -1
      return a[0].localeCompare(b[0])
    })
    .map(([key, ppl]) => ({ name: groupLabel(key), people: ppl }))
}
